import moment from 'moment';

import { CanceledStatuses, subscriptionStatus } from '../../Model/Subscription';
import { toMomentUnit } from '../../utils/durationUnit';
import { reportOnce } from '../../utils/userCoachData';
import {
  ClientStatus,
  filterChecks,
  isEligibleForActiveSubscriptionAmendment,
} from './statusFilter';

/*
  A billing target is one contract of the client plus everything an action on it needs: the
  user-doc `contracts` entry (its subscription state), the `userContract` doc (its terms and
  product name) and the Stripe subscription it was created for. A client may hold one
  contract per product with the same coach, so the client's rolled-up status cannot answer a
  billing question - one paused product and one active product has to offer both Pause and
  Unpause.
*/

const isChangeableStatus = (status) => !!status && !CanceledStatuses.includes(status);

const UNNAMED_PRODUCT = 'Unnamed product';

const productLabel = (target) => target?.productName || UNNAMED_PRODUCT;

const stripeSubscriptionOf = (target) => target?.subscriptionDoc?.subscriptionData;

const isOnBreak = (clientView) => moment(clientView?.breakEndDate).isAfter(moment());

/*
  Mirrors the server: an exact `metadata.contractId` match wins, and a subscription without
  that metadata is assumed to be the contract's only when there is nothing to confuse it
  with. Several of either is ambiguous, and guessing points the action at the wrong product.
*/
const mapSubscriptionsToContracts = (subscriptionDocs, entryCount) => {
  const byContractId = new Map();
  const withoutContract = [];
  subscriptionDocs.forEach((doc) => {
    if (doc.contractId) {
      byContractId.set(doc.contractId, doc);
    } else {
      withoutContract.push(doc);
    }
  });
  const assumed = (entryCount === 1 && withoutContract.length === 1) ? withoutContract[0] : null;
  return { byContractId, assumed };
};

/*
  The user-doc entries drive the list because they are the only source of `isActive` and
  `subscriptionStatus`. A `current` contract doc with no entry is reported and left out
  rather than acted on blind.
*/
const buildBillingTargets = ({ userDoc, contractDocs = [], subscriptionDocs = [] }) => {
  const contractDocById = new Map(contractDocs.map((doc) => [doc.id, doc]));
  const coachEntries = userDoc?.contractEntries || [];
  /*
    Entries seeded at checkout carry no `coachId` until activation adds it, so a client
    mid-payment would otherwise have no contracts here at all. The contract doc naming this
    coach is what establishes ownership for those.
  */
  const adopted = contractDocs
    .filter((doc) => doc.coach === userDoc?.resolvedCoachId)
    .filter((doc) => !coachEntries.some(({ contractId }) => contractId === doc.id))
    .map((doc) => userDoc.contractEntryById(doc.id))
    .filter((entry) => !!entry && !entry.coachId);
  const entries = [...coachEntries, ...adopted];
  const { byContractId, assumed } = mapSubscriptionsToContracts(subscriptionDocs, entries.length);
  const entryIds = new Set(entries.map(({ contractId }) => contractId));

  contractDocs
    .filter((doc) => doc.current && doc.coach === userDoc.resolvedCoachId && !entryIds.has(doc.id))
    .forEach((doc) => reportOnce(
      `orphanContract:${userDoc.id}:${doc.id}`,
      'Current contract doc has no contracts entry on the user doc',
      { userId: userDoc.id, contractId: doc.id },
    ));

  return entries
    .map((contractEntry) => {
      const contractDoc = contractDocById.get(contractEntry.contractId) || null;
      return {
        contractId: contractEntry.contractId,
        productId: contractEntry.productId,
        productName: contractDoc?.productName || '',
        contractEntry,
        contractDoc,
        subscriptionDoc: byContractId.get(contractEntry.contractId) || assumed,
        clientView: userDoc.contractStatusView(contractEntry.contractId),
      };
    })
    .sort((first, second) => (
      (second.clientView.serviceStartAt?.getTime() || 0) - (first.clientView.serviceStartAt?.getTime() || 0)
    ));
};

const isLiveTarget = (target) => isChangeableStatus(target.clientView?.subscriptionStatus);

const isEndedTarget = (target) => (
  !!target.clientView?.subscriptionStatus && CanceledStatuses.includes(target.clientView.subscriptionStatus)
);

const startedAtMillis = (target) => target.clientView?.serviceStartAt?.getTime() || 0;
const createdAtMillis = (target) => target.contractDoc?.createdAt?.toMillis?.() || 0;

const isPreferredTarget = (candidate, incumbent) => {
  const isCandidateCurrent = !!candidate.contractDoc?.current;
  if (isCandidateCurrent !== !!incumbent.contractDoc?.current) {
    return isCandidateCurrent;
  }
  const startDifference = startedAtMillis(candidate) - startedAtMillis(incumbent);
  if (startDifference !== 0) {
    return startDifference > 0;
  }
  return createdAtMillis(candidate) > createdAtMillis(incumbent);
};

/**
 * The one contract per product an action applies to: the contract in force, or the most recent
 * where none is. Contracts predating the `current` flag all read as current, so recency decides
 * those. Call with candidates that already passed their eligibility check, never before it.
 */
const oneTargetPerProduct = (targets) => {
  const keyOf = (target) => target.productId || target.contractId;
  const bestByProduct = new Map();
  targets.forEach((target) => {
    const key = keyOf(target);
    const incumbent = bestByProduct.get(key);
    if (!incumbent || isPreferredTarget(target, incumbent)) {
      bestByProduct.set(key, target);
    }
  });
  return targets.filter((target) => bestByProduct.get(keyOf(target)) === target);
};

/**
 * The targets a client-facing section lists: one per live product, falling back to the coach's
 * best contract when nothing is live, so a fully cancelled client still reads as one client
 * rather than every product they ever held. Several entries can name the same product —
 * amendments and re-onboarding mint a new contract id for one — so the live set is deduped.
 *
 * The fallback never empties the list. `contractView` is coach-scoped, so a contract seeded at
 * checkout — which carries no `coachId` until activation — leaves no best contract to rank by,
 * and filtering on it would blank the screen for a client mid-payment.
 *
 * @param {Array<object>} targets - The client's billing targets.
 * @param {string} [bestContractId] - `userDoc.contractView.contractId`.
 * @return {Array<object>} The targets to show.
 */
const listedTargets = (targets, bestContractId) => {
  const live = targets.filter(isLiveTarget);
  if (live.length) {
    return oneTargetPerProduct(live);
  }
  const best = targets.filter((target) => target.contractId === bestContractId);
  return best.length ? best : oneTargetPerProduct(targets);
};

/*
  A challenge is normally a one-time payment, so there is no Stripe subscription to cancel: it ends
  through its own server action, which tears the contract down the way the nightly cron does when
  the window passes. One created from a contract can carry a live Stripe subscription though, and
  that one has to be cancelled in Stripe like any other — the server refuses to end it locally — so
  only a challenge with no subscription doc takes the challenge action.
*/
const isChallengeTarget = (target) => !!target?.contractDoc?.challengeId;

const isOneTimeChallengeTarget = (target) => isChallengeTarget(target) && !target.subscriptionDoc;

const canCancelSubscription = (target) => (!!target.subscriptionDoc || isOneTimeChallengeTarget(target))
  && isLiveTarget(target);

const canUpdateStartDate = (target) => (
  !!target.subscriptionDoc
  && isLiveTarget(target)
  && filterChecks[ClientStatus.FUTURE_START](target.clientView)
);

// Pausing inside a paid-in-full or discounted first period would hand back the discount on resumption.
const isPastPrepaidPeriod = (clientView, contractDoc) => {
  const {
    initialTerm,
    initialPaymentInCents,
    totalPriceInCents,
    upfrontPaymentInCents,
    durationUnit,
  } = contractDoc;
  const { serviceStartAt } = clientView;
  const isAfterInitialTerm = moment().isAfter(moment(serviceStartAt).add(initialTerm, toMomentUnit(durationUnit)));
  const hasStarted = moment().isAfter(moment(serviceStartAt));
  const isSingleUndiscountedMonth = initialTerm === 1
    && initialPaymentInCents === totalPriceInCents
    && !upfrontPaymentInCents;
  return isAfterInitialTerm || (hasStarted && isSingleUndiscountedMonth);
};

// A differing post-commitment price means resumption would silently reprice the contract.
const isPostCommitmentPriceSettled = (clientView, contractDoc) => {
  const {
    minSubscriptionMonths,
    postCommitmentPriceInCents,
    totalPriceInCents,
    durationUnit,
  } = contractDoc;
  if (!postCommitmentPriceInCents || postCommitmentPriceInCents === totalPriceInCents) {
    return true;
  }
  const commitmentEnd = moment(clientView.serviceStartAt).add(minSubscriptionMonths, toMomentUnit(durationUnit));
  return moment().isAfter(commitmentEnd);
};

const canPauseSubscription = (target) => {
  const { clientView, contractDoc, subscriptionDoc } = target;
  // A challenge is a one-time purchase, so there is nothing to pause, even when a subscription bills it.
  if (!subscriptionDoc || !contractDoc || isChallengeTarget(target) || !isLiveTarget(target)) {
    return false;
  }
  return !isOnBreak(clientView)
    && isPastPrepaidPeriod(clientView, contractDoc)
    && isPostCommitmentPriceSettled(clientView, contractDoc);
};

const canUnpauseSubscription = (target) => {
  const { clientView, subscriptionDoc } = target;
  if (!subscriptionDoc || !clientView?.breakEndDate) {
    return false;
  }
  return isOnBreak(clientView) && clientView.subscriptionStatus === subscriptionStatus.ACTIVE;
};

/*
  TRIALING counts alongside ACTIVE because the server's `contracts.{id}.isActive` is
  `[ACTIVE, TRIALING]`. Leaving it out sends a trialing client down the re-onboarding path,
  which the sign-up duplicate guard then refuses — their product is still live.
*/
const AMENDABLE_STATUSES = [subscriptionStatus.ACTIVE, subscriptionStatus.TRIALING];

const canAmendContract = (target) => (
  !!target.subscriptionDoc
  && AMENDABLE_STATUSES.includes(target.clientView?.subscriptionStatus)
  && isEligibleForActiveSubscriptionAmendment(target.clientView)
);

/** Why an amendment is unavailable when no client status explains it. */
const AmendmentBlocker = {
  NO_SUBSCRIPTION: 'NO_SUBSCRIPTION',
};

const amendmentBlockerFor = (target) => {
  const { clientView } = target;
  if (canAmendContract(target)) {
    return '';
  }
  // Before the status checks: an active client can still hold a product with nothing to amend.
  if (!target.subscriptionDoc) {
    return AmendmentBlocker.NO_SUBSCRIPTION;
  }
  if (filterChecks[ClientStatus.ON_BREAK](clientView)) {
    return ClientStatus.ON_BREAK;
  }
  if (filterChecks[ClientStatus.FUTURE_START](clientView)) {
    return ClientStatus.FUTURE_START;
  }
  return ClientStatus.INACTIVE;
};

/*
  A cancelled contract is excluded rather than merely inactive: `!isActive` is true for it, so
  the chooser would offer a product the client no longer holds and activating it would stamp
  service dates onto a dead contract. The route back for a churned client is re-onboarding,
  which `canReonboardClient` below offers on exactly that state.
*/
const canActivateOnContract = (target) => (
  !isEndedTarget(target)
  && (!target.clientView?.isActive || target.clientView.subscriptionStatus === subscriptionStatus.TRIALING)
);

/*
  Re-onboarding is withheld while anything is live - the action wanted there is new terms on
  the running product. It needs no contract chosen: it mints a new one, and the product is
  picked in the modal.
*/
const canReonboardClient = (targets) => (
  targets.length > 0 && !targets.some(isLiveTarget) && targets.some(isEndedTarget)
);

// Stripe moved the subscription reference under `parent` and kept the flat field on older versions.
const invoiceContractId = (invoice) => invoice?.parent?.subscription_details?.metadata?.contractId;

const invoiceSubscriptionId = (invoice) => {
  const flat = invoice?.subscription;
  if (typeof flat === 'string') {
    return flat;
  }
  return flat?.id || invoice?.parent?.subscription_details?.subscription;
};

/*
  Separate from resolving the target: an invoice can name a subscription this coach has no
  contract for - another coach's, or one missing from the user doc - and calling that a
  one-time payment would misdescribe a real subscription charge.
*/
const isSubscriptionInvoice = (invoice) => !!invoiceContractId(invoice) || !!invoiceSubscriptionId(invoice);

const invoiceMatchesTarget = (target, invoice) => {
  const contractId = invoiceContractId(invoice);
  if (contractId) {
    return target.contractId === contractId;
  }
  const subscriptionId = invoiceSubscriptionId(invoice);
  return !!subscriptionId && target.subscriptionDoc?.id === subscriptionId;
};

const findTargetForInvoice = (targets, invoice) => (
  targets.find((target) => invoiceMatchesTarget(target, invoice)) || null
);

/*
  Scoped to the contract because a client's invoices span every product they hold, so the
  newest one overall can belong to another product - and paying that takes money for the
  wrong thing.
*/
const findRetryableInvoiceForContract = (invoices, contractId, subscriptionDoc) => {
  const target = { contractId, subscriptionDoc: subscriptionDoc || null };
  return (invoices || []).reduce((newest, invoice) => {
    if (invoice.status !== 'open' || !invoiceMatchesTarget(target, invoice)) {
      return newest;
    }
    return (!newest || invoice.created > newest.created) ? invoice : newest;
  }, null);
};

export {
  AMENDABLE_STATUSES,
  amendmentBlockerFor,
  AmendmentBlocker,
  buildBillingTargets,
  canActivateOnContract,
  canAmendContract,
  canCancelSubscription,
  canPauseSubscription,
  canReonboardClient,
  canUnpauseSubscription,
  canUpdateStartDate,
  findRetryableInvoiceForContract,
  findTargetForInvoice,
  isChallengeTarget,
  isLiveTarget,
  isOneTimeChallengeTarget,
  isSubscriptionInvoice,
  listedTargets,
  oneTargetPerProduct,
  productLabel,
  stripeSubscriptionOf,
};
